// frontend/src/components/admin/content/LessonTopicsPanel.tsx

import { useEffect, useState, useCallback } from 'react';
import { adminService } from '../../../services/admin.service';
import { AdminLesson, AdminLessonTopic } from '../../../types/admin.types';
import { TopicFormModal } from './TopicFormModal';

interface LessonTopicsPanelProps {
  lesson: AdminLesson;
  onClose: () => void;
}

export function LessonTopicsPanel({ lesson, onClose }: LessonTopicsPanelProps) {
  const [topics, setTopics] = useState<AdminLessonTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<AdminLessonTopic | null>(null);
  const [showForm, setShowForm] = useState(false);

  const reload = useCallback(() => {
    setIsLoading(true);
    adminService
      .listLessonTopics(lesson.id)
      .then(setTopics)
      .catch((err) => console.error('Konular yüklenemedi:', err))
      .finally(() => setIsLoading(false));
  }, [lesson.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleDelete(topicId: string) {
    if (!confirm('Bu konuyu silmek istediğinize emin misiniz? AI hoca sunumu bu konuyu içermeyecektir.')) return;
    try {
      await adminService.deleteLessonTopic(topicId);
      reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Konu silinemedi.');
    }
  }

  function handleEdit(topic: AdminLessonTopic) {
    setSelectedTopic(topic);
    setShowForm(true);
  }

  function handleAdd() {
    setSelectedTopic(null);
    setShowForm(true);
  }

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={(e) => e.stopPropagation()}>
        <h3>{lesson.title} — Konu Yönetimi</h3>
        <p style={{ color: 'var(--gray)', fontSize: 13, marginBottom: 16 }}>
          Bu derse ait alt konuları yönetin. AI hoca slaytları buradaki konuları sırayla temel alarak oluşturur.
        </p>

        {isLoading ? (
          <div className="courses-loading">Konular Yükleniyor...</div>
        ) : (
          <div className="course-detail-lesson-list" style={{ marginBottom: 16, maxHeight: 350, overflowY: 'auto' }}>
            {topics.length === 0 && <p className="parent-empty-text">Henüz konu eklenmemiş.</p>}
            {topics.map((topic, i) => (
              <div
                key={topic.id}
                className="course-detail-lesson-item"
                style={{ cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                  <div className="course-detail-lesson-number">{topic.order_index}</div>
                  <div>
                    <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {topic.title}
                      {topic.is_premium && (
                        <span className="lesson-badge" style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--primary-light)', fontSize: 10, padding: '2px 6px' }}>
                          Premium
                        </span>
                      )}
                    </h4>
                    {topic.description && (
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--gray)' }}>
                        {topic.description}
                      </p>
                    )}
                    {topic.video_url && (
                      <div style={{ fontSize: 11, color: 'var(--secondary)', marginTop: 4 }}>
                        Destekleyici video bağlantısı mevcut
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="lesson-btn secondary sm" onClick={() => handleEdit(topic)}>
                    Düzenle
                  </button>
                  <button type="button" className="admin-remove-btn" onClick={() => handleDelete(topic.id)}>
                    Sil
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
          <button type="button" className="lesson-btn primary sm" onClick={handleAdd}>
            Yeni Konu Ekle
          </button>
          <button type="button" className="lesson-btn secondary" onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>

      {showForm && (
        <TopicFormModal
          lessonId={lesson.id}
          topic={selectedTopic}
          onClose={() => setShowForm(false)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
